'use client';

import { useState, useEffect, useRef, type ReactElement } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useAuthStore } from '@/stores/authStore';
import { useProjectStore } from '@/stores/projectStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useNotificationWS } from '@/hooks/useNotificationWS';
import { settingsApi } from '@/lib/api';

interface MenuItem {
  href?: string;
  label: string;
  icon: string;
  disabled?: boolean;
  hidden?: boolean;
  requiresGithub?: boolean;
  children?: MenuItem[];
}

const menuStructure: MenuItem[] = [
  // 1. 프로젝트 (착수)
  {
    label: '프로젝트',
    icon: 'project',
    children: [
      { href: '/project/dashboard', label: '프로젝트 대시보드', icon: 'dashboard' },
      { href: '/project/create', label: '프로젝트 생성', icon: 'edit' },
      // { href: '#', label: '프로젝트 상태', icon: 'status', hidden: true },
    ],
  },
  // 2. 사업관리 (프로젝트 전반)
  {
    label: '사업관리',
    icon: 'management',
    children: [
      { href: '/', label: 'WBS', icon: 'chart' },
      { href: '/reports', label: '업무보고서', icon: 'document' },
      { href: '/management/meetings', label: '회의록', icon: 'document' },
      { href: '/analysis/traceability', label: '추적성 매트릭스', icon: 'traceability' },
      { href: '/management/members', label: '인력투입현황', icon: 'team' },
      { href: '/management/client-portal', label: '고객 포털 관리', icon: 'team' },
    ],
  },
  // 3. 분석
  {
    label: '분석',
    icon: 'analysis',
    children: [
      { href: '/analysis/rfp', label: 'RFP 분석', icon: 'document' },
      { href: '/analysis/requirements', label: '요구사항 목록', icon: 'list' },
      { href: '/analysis/frd', label: '요구사항 정의서', icon: 'clipboard' },
    ],
  },
  // 3. 설계
  {
    label: '설계',
    icon: 'design',
    children: [
      { href: '/design/architecture', label: '시스템 아키텍처', icon: 'architecture' },
      { href: '/menu-structure', label: '메뉴 구조도', icon: 'folder' },
      { href: '/design/fsd', label: '기능명세서', icon: 'clipboard' },
      { href: '/design/ui', label: '화면 설계서', icon: 'screen' },
      { href: '/design/erd', label: 'ERD', icon: 'database' },
      { href: '/design/table-definitions', label: '테이블정의서', icon: 'list' },
      { href: '/design/api-spec', label: 'API 명세서', icon: 'api' },
      // { href: '#', label: '데이터 흐름도', icon: 'flow', hidden: true },
      // { href: '#', label: '성능 요구사항', icon: 'status', hidden: true },
      // { href: '#', label: '보안 설계서', icon: 'security', hidden: true },
    ],
  },
  // 4. 구현
  {
    label: '구현',
    icon: 'code',
    children: [
      {
        label: '디자인',
        icon: 'palette',
        children: [
          { href: '/implementation/design/style-guide', label: '웹스타일가이드', icon: 'palette' },
          { href: '/implementation/design/assets', label: '디자인 산출물', icon: 'image' },
        ],
      },
      {
        label: '퍼블리싱',
        icon: 'html',
        children: [
          { href: '/implementation/publishing/guide', label: '퍼블리싱 가이드', icon: 'document' },
          { href: '/implementation/publishing/accessibility', label: '접근성 검증', icon: 'check' },
          // { href: '#', label: '퍼블리싱 산출물', icon: 'folder', hidden: true },
        ],
      },
      {
        label: '개발',
        icon: 'git',
        children: [
          { href: '/development/dev-environment', label: '개발환경 설정', icon: 'settings' },
          { href: '/development/coding-convention', label: '코딩 컨벤션', icon: 'clipboard' },
          { href: '/development/common-modules', label: '공통 모듈 가이드', icon: 'document' },
          { href: '/development/tasks', label: '개발 작업 목록', icon: 'list' },
          { href: '/development/code-docs', label: '코드 문서', icon: 'code', requiresGithub: true },
        ],
      },
    ],
  },
  // 5. 테스트
  {
    label: '테스트',
    icon: 'test',
    children: [
      { href: '/development/test-scenarios', label: '테스트 시나리오', icon: 'scenario' },
      { href: '/development/test-cases', label: '테스트 케이스', icon: 'testcase' },
      { href: '/development/test-runs', label: '테스트 실행', icon: 'play' },
      { href: '/development/test-results', label: '테스트 결과', icon: 'chart' },
    ],
  },
  // 6. 이행
  {
    label: '이행',
    icon: 'deploy',
    children: [
      { href: '/deployment/plan', label: '이행계획서', icon: 'plan' },
      { href: '/deployment/environments', label: '환경구성', icon: 'server' },
      { href: '/deployment/migration', label: '데이터이행', icon: 'database' },
      { href: '/deployment/releases', label: '배포관리', icon: 'release' },
      { href: '/deployment/handover', label: '운영인수인계', icon: 'handover' },
    ],
  },
  // 7. 종료
  {
    label: '종료',
    icon: 'close',
    children: [
      { href: '/closing/deliverables', label: '프로젝트 산출물', icon: 'archive' },
      { href: '/closing/export', label: '산출물 출력', icon: 'document' },
      // { href: '#', label: '종료 보고서', icon: 'report', hidden: true },
      // { href: '#', label: '회고/개선점', icon: 'feedback', hidden: true },
    ],
  },
  // 8. 설정
  {
    label: '설정',
    icon: 'settings',
    children: [
      { href: '/settings/categories', label: '카테고리 관리', icon: 'list' },
      { href: '/settings/common-codes', label: '공통코드 관리', icon: 'code' },
      { href: '/settings/system', label: '시스템 설정', icon: 'settings' },
      // { href: '#', label: '사용자 관리', icon: 'team', hidden: true },
    ],
  },
  // 9. MCP 가이드
  {
    label: 'MCP 가이드',
    icon: 'mcp',
    children: [
      { href: '/development/mcp-guide', label: 'MCP 연결 가이드', icon: 'api' },
    ],
  },
  // 10. 디버그 (임시)
  {
    label: '디버그',
    icon: 'bug',
    children: [
      { href: '/debug/traceability-chain', label: '추적성 체인', icon: 'traceability' },
    ],
  },
  // 11. AI 어시스턴트
  {
    label: 'AI 어시스턴트',
    icon: 'ai',
    children: [
      { href: '#ai-chat', label: 'AI 채팅', icon: 'chat' },
    ],
  },
];

// Icons
const getIcon = (name: string, active: boolean, disabled?: boolean): ReactElement => {
  const color = disabled ? 'text-gray-600' : active ? 'text-blue-400' : 'text-gray-400';
  const baseClass = `w-4 h-4 ${color}`;

  const icons: Record<string, ReactElement> = {
    // Main category icons
    project: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
    analysis: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
    design: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
      </svg>
    ),
    code: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    test: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    deploy: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
      </svg>
    ),
    close: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    management: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    // Sub-item icons
    chart: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    clipboard: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
    folder: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
      </svg>
    ),
    document: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    list: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
      </svg>
    ),
    dashboard: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
      </svg>
    ),
    edit: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
    status: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    lightbulb: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    check: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    architecture: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
    screen: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    database: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
      </svg>
    ),
    api: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    flow: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
      </svg>
    ),
    security: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    traceability: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
    git: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    testcase: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
    scenario: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    ai: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
      </svg>
    ),
    chat: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
    mcp: (
      <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    bug: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    plan: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    checklist: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
    handover: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
      </svg>
    ),
    archive: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
      </svg>
    ),
    report: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    feedback: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
    team: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    palette: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
      </svg>
    ),
    image: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    html: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    settings: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    server: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
      </svg>
    ),
    release: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
      </svg>
    ),
    play: (
      <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  };

  return icons[name] || icons.document;
};

interface SidebarProps {
  isExpanded: boolean;
  onToggle: () => void;
  onOpenChat?: () => void;
}

export default function Sidebar({ isExpanded, onToggle, onOpenChat }: SidebarProps) {
  const pathname = usePathname();
  const { user, logout } = useAuthStore();
  const { currentProjectId, currentProject, projects, setProject } = useProjectStore();
  const {
    notifications, unreadCount, isOpen,
    toggle: toggleNotifications, setOpen: setNotificationsOpen,
    markAsRead, markAllAsRead, fetchNotifications, fetchUnreadCount,
  } = useNotificationStore();
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const visitedMenusRef = useRef<Set<string>>(new Set());
  const [githubEnabled, setGithubEnabled] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  // WebSocket 알림 연결
  useNotificationWS();

  // GitHub 설정 가져오기
  useEffect(() => {
    const fetchGithubSetting = async () => {
      try {
        const setting = await settingsApi.getByKey('github_enabled');
        setGithubEnabled(setting?.value === 'true');
      } catch {
        // 설정이 없으면 비활성화 상태 유지
        setGithubEnabled(false);
      }
    };
    fetchGithubSetting();
  }, []);

  // 알림 패널 외부 클릭 닫기
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotificationsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, setNotificationsOpen]);

  const isActiveItem = (href?: string) => {
    if (!href || href === '#') return false;
    return pathname === href;
  };

  const hasActiveChild = (item: MenuItem): boolean => {
    if (!item.children) return false;
    return item.children.some((child) =>
      isActiveItem(child.href) || hasActiveChild(child)
    );
  };

  // 현재 경로에 해당하는 메뉴 키들을 찾는 함수
  const findActiveMenuKeys = (): string[] => {
    const keys: string[] = [];

    for (const section of menuStructure) {
      if (hasActiveChild(section)) {
        keys.push(section.label);

        // 3뎁스 구조인 경우 서브메뉴도 확인
        if (section.children) {
          for (const child of section.children) {
            if (child.children && hasActiveChild(child)) {
              keys.push(`${section.label}-${child.label}`);
            }
          }
        }
      }
    }

    return keys;
  };

  // 경로 변경 시 현재 위치의 메뉴와 방문한 메뉴들 자동 확장
  useEffect(() => {
    const activeKeys = findActiveMenuKeys();

    // 방문한 메뉴 기록
    activeKeys.forEach(key => visitedMenusRef.current.add(key));

    // 현재 활성 메뉴와 방문한 메뉴 모두 확장
    setExpandedMenus(prev => {
      const next = new Set(prev);
      activeKeys.forEach(key => next.add(key));
      visitedMenusRef.current.forEach(key => next.add(key));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const toggleMenu = (label: string) => {
    setExpandedMenus((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
        // 열었을 때 방문 기록에 추가
        visitedMenusRef.current.add(label);
      }
      return next;
    });
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  return (
    <aside
      className={`bg-[#0C1929] flex flex-col h-screen transition-all duration-300 ease-in-out ${
        isExpanded ? 'w-60' : 'w-16'
      }`}
    >
      {/* Header */}
      <div className={`flex items-center h-14 border-b border-[#1D3A5C] ${isExpanded ? 'px-3' : 'px-0 justify-center'}`}>
        {isExpanded ? (
          <img src="/logo.png" alt="OmniMind OS" className="h-9 object-contain" />
        ) : (
          <img src="/omnimind-icon-sm.png" alt="OmniMind OS" className="w-8 h-8 rounded-lg" />
        )}
      </div>

      {/* Project Selector */}
      {projects.length > 0 && (
        <div className={`border-b border-[#1D3A5C] ${isExpanded ? 'px-3 py-2' : 'px-1 py-2 flex justify-center'}`}>
          {isExpanded ? (
            <select
              value={currentProjectId || ''}
              onChange={(e) => setProject(Number(e.target.value))}
              style={{ color: '#ffffff', backgroundColor: '#1D3A5C' }}
              className="w-full text-xs rounded px-2 py-1.5 border border-[#2D4A6C] focus:outline-none focus:border-blue-500 cursor-pointer"
            >
              {projects.map(p => (
                <option key={p.id} value={p.id} className="bg-[#0C1929] text-white">{p.name}</option>
              ))}
            </select>
          ) : (
            <div
              className="w-8 h-8 rounded bg-[#1D3A5C] flex items-center justify-center text-xs font-bold text-blue-300 cursor-pointer"
              title={currentProject?.name || '프로젝트'}
            >
              {currentProject?.code?.slice(0, 2) || 'P'}
            </div>
          )}
        </div>
      )}

      {/* Toggle Button */}
      <button
        onClick={onToggle}
        className={`flex items-center h-10 text-[#8B9DC3] hover:text-white hover:bg-[#1D3A5C] transition ${
          isExpanded ? 'px-4 justify-start gap-3' : 'justify-center'
        }`}
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          {isExpanded ? (
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          )}
        </svg>
        {isExpanded && <span className="text-sm">메뉴 접기</span>}
      </button>

      {/* Navigation */}
      <nav className="flex-1 py-2 overflow-y-auto">
        {isExpanded ? (
          <ul className="space-y-0.5">
            {menuStructure.map((section) => {
              const isExpanded = expandedMenus.has(section.label);
              const isActive = hasActiveChild(section);

              return (
                <li key={section.label}>
                  {/* Section Header */}
                  <button
                    onClick={() => toggleMenu(section.label)}
                    className={`w-full flex items-center h-9 px-4 gap-2 text-left transition ${
                      isActive
                        ? 'text-blue-400'
                        : 'text-[#8B9DC3] hover:text-white hover:bg-[#1D3A5C]'
                    }`}
                  >
                    {getIcon(section.icon, isActive)}
                    <span className={`text-sm font-medium flex-1 ${section.icon === 'mcp' ? 'text-emerald-400' : ''}`}>{section.label}</span>
                    <svg
                      className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Sub Items */}
                  {isExpanded && section.children && (
                    <ul className="mt-0.5 mb-1">
                      {section.children.map((child) => {
                        // 3뎁스 구조 체크 (children이 있는지)
                        if (child.children) {
                          const isSubExpanded = expandedMenus.has(`${section.label}-${child.label}`);
                          const hasActive = hasActiveChild(child);

                          return (
                            <li key={child.label}>
                              {/* 2뎁스 헤더 (토글 가능) */}
                              <button
                                onClick={() => toggleMenu(`${section.label}-${child.label}`)}
                                className={`w-full flex items-center h-8 pl-10 pr-4 gap-2 text-left transition ${
                                  hasActive
                                    ? 'text-blue-400'
                                    : 'text-[#8B9DC3] hover:text-white hover:bg-[#1D3A5C]'
                                }`}
                              >
                                {getIcon(child.icon, hasActive)}
                                <span className="text-xs font-medium flex-1">{child.label}</span>
                                <svg
                                  className={`w-3 h-3 transition-transform ${isSubExpanded ? 'rotate-180' : ''}`}
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>

                              {/* 3뎁스 아이템들 */}
                              {isSubExpanded && (
                                <ul className="mt-0.5">
                                  {child.children.map((subChild) => {
                                    // GitHub 필요 메뉴는 설정에 따라 숨김
                                    if (subChild.requiresGithub && !githubEnabled) {
                                      return null;
                                    }

                                    const subActive = isActiveItem(subChild.href);
                                    const subDisabled = subChild.disabled;

                                    if (subDisabled) {
                                      return (
                                        <li key={subChild.label}>
                                          <span
                                            className="flex items-center h-7 pl-14 pr-4 gap-2 text-gray-600 cursor-not-allowed"
                                            title="준비 중"
                                          >
                                            {getIcon(subChild.icon, false, true)}
                                            <span className="text-[11px]">{subChild.label}</span>
                                            <span className="ml-auto text-[9px] bg-gray-700 text-gray-500 px-1 py-0.5 rounded">
                                              준비중
                                            </span>
                                          </span>
                                        </li>
                                      );
                                    }

                                    return (
                                      <li key={subChild.label}>
                                        <Link
                                          href={subChild.href || '/'}
                                          className={`flex items-center h-7 pl-14 pr-4 gap-2 transition relative ${
                                            subActive
                                              ? 'bg-[#1D3A5C] text-white'
                                              : 'text-[#8B9DC3] hover:bg-[#1D3A5C] hover:text-white'
                                          }`}
                                        >
                                          {subActive && (
                                            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-4 bg-blue-500 rounded-r" />
                                          )}
                                          {getIcon(subChild.icon, subActive)}
                                          <span className="text-[11px]">{subChild.label}</span>
                                        </Link>
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </li>
                          );
                        }

                        // 2뎁스 일반 아이템 (href가 있는 경우)
                        const active = isActiveItem(child.href);
                        const disabled = child.disabled;

                        if (disabled) {
                          return (
                            <li key={child.label}>
                              <span
                                className="flex items-center h-8 pl-10 pr-4 gap-2 text-gray-600 cursor-not-allowed"
                                title="준비 중"
                              >
                                {getIcon(child.icon, false, true)}
                                <span className="text-xs">{child.label}</span>
                                <span className="ml-auto text-[10px] bg-gray-700 text-gray-500 px-1.5 py-0.5 rounded">
                                  준비중
                                </span>
                              </span>
                            </li>
                          );
                        }

                        // AI 채팅 특수 처리
                        if (child.href === '#ai-chat' && onOpenChat) {
                          return (
                            <li key={child.label}>
                              <button
                                onClick={onOpenChat}
                                className="w-full flex items-center h-8 pl-10 pr-4 gap-2 transition text-blue-300 hover:bg-blue-600/20 hover:text-white"
                              >
                                {getIcon(child.icon, false)}
                                <span className="text-xs">{child.label}</span>
                              </button>
                            </li>
                          );
                        }

                        return (
                          <li key={child.label}>
                            <Link
                              href={child.href || '/'}
                              className={`flex items-center h-8 pl-10 pr-4 gap-2 transition relative ${
                                active
                                  ? 'bg-[#1D3A5C] text-white'
                                  : 'text-[#8B9DC3] hover:bg-[#1D3A5C] hover:text-white'
                              }`}
                            >
                              {active && (
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-blue-500 rounded-r" />
                              )}
                              {getIcon(child.icon, active)}
                              <span className="text-xs">{child.label}</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          /* Collapsed state - show only main category icons */
          <ul className="space-y-1">
            {menuStructure.map((section) => {
              const isActive = hasActiveChild(section);

              return (
                <li key={section.label} className="relative group">
                  <button
                    onClick={() => {
                      onToggle();
                      setTimeout(() => {
                        setExpandedMenus((prev) => new Set(prev).add(section.label));
                      }, 100);
                    }}
                    className={`w-full flex items-center justify-center h-10 transition ${
                      isActive
                        ? 'text-blue-400 bg-[#1D3A5C]'
                        : 'text-[#8B9DC3] hover:text-white hover:bg-[#1D3A5C]'
                    }`}
                  >
                    {getIcon(section.icon, isActive)}
                  </button>
                  {/* Tooltip */}
                  <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition whitespace-nowrap z-50">
                    {section.label}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </nav>

      {/* Notification Bell */}
      <div className={`relative border-t border-[#1D3A5C] ${isExpanded ? 'px-3 py-2' : 'px-2 py-2'}`} ref={notifRef}>
        {isExpanded ? (
          <button
            onClick={toggleNotifications}
            className="w-full flex items-center gap-2 px-2 py-1.5 text-[#8B9DC3] hover:text-white hover:bg-[#1D3A5C] rounded text-sm transition relative"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <span>알림</span>
            {unreadCount > 0 && (
              <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
        ) : (
          <button
            onClick={toggleNotifications}
            className="w-full flex justify-center p-1.5 text-[#8B9DC3] hover:text-white hover:bg-[#1D3A5C] rounded transition relative group"
            title="알림"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute top-0 right-1 bg-red-500 text-white text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
            <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition whitespace-nowrap z-50">
              알림 {unreadCount > 0 ? `(${unreadCount})` : ''}
            </div>
          </button>
        )}

        {/* Notification Dropdown */}
        {isOpen && (
          <div className="absolute left-full bottom-0 ml-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <span className="text-sm font-semibold text-gray-800">알림</span>
              {unreadCount > 0 && (
                <button onClick={markAllAsRead} className="text-xs text-blue-600 hover:text-blue-800">
                  전체 읽음
                </button>
              )}
            </div>
            <div className="overflow-y-auto flex-1">
              {notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-400">알림이 없습니다</div>
              ) : (
                notifications.map(n => (
                  <button
                    key={n.id}
                    onClick={async () => {
                      if (!n.is_read) await markAsRead(n.id);
                      if (n.link) window.location.href = n.link;
                      setNotificationsOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition ${!n.is_read ? 'bg-blue-50/50' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-sm mt-0.5 flex-shrink-0">
                        {n.type === 'mention' ? '@' : n.type === 'comment' ? '💬' : n.type === 'deadline' ? '⏰' : '🔔'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!n.is_read ? 'font-medium text-gray-900' : 'text-gray-600'}`}>{n.title}</p>
                        {n.content && <p className="text-xs text-gray-400 mt-0.5 truncate">{n.content}</p>}
                        <p className="text-[10px] text-gray-400 mt-1">
                          {(() => {
                            const diff = Date.now() - new Date(n.created_at).getTime();
                            const m = Math.floor(diff / 60000);
                            if (m < 1) return '방금';
                            if (m < 60) return `${m}분 전`;
                            const h = Math.floor(m / 60);
                            if (h < 24) return `${h}시간 전`;
                            return `${Math.floor(h / 24)}일 전`;
                          })()}
                        </p>
                      </div>
                      {!n.is_read && <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></div>}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* User Section */}
      <div className={`border-t border-[#1D3A5C] ${isExpanded ? 'p-3' : 'p-2'}`}>
        {isExpanded ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#1D3A5C] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-blue-400 font-medium text-sm">
                  {user?.name?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user?.name}</p>
                <p className="text-[#8B9DC3] text-xs truncate">{user?.company}</p>
              </div>
              <span className={`px-1.5 py-0.5 text-[10px] rounded ${
                user?.role === 'super_admin'
                  ? 'bg-purple-500/20 text-purple-400'
                  : user?.role === 'admin'
                    ? 'bg-blue-500/20 text-blue-400'
                    : 'bg-gray-500/20 text-gray-400'
              }`}>
                {user?.role === 'super_admin' ? '최고관리자' : user?.role === 'admin' ? '관리자' : '뷰어'}
              </span>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-[#8B9DC3] hover:text-white hover:bg-[#1D3A5C] rounded text-sm transition"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              로그아웃
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 bg-[#1D3A5C] rounded-full flex items-center justify-center group relative">
              <span className="text-blue-400 font-medium text-sm">
                {user?.name?.charAt(0) || 'U'}
              </span>
              {/* Tooltip */}
              <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition whitespace-nowrap z-50">
                {user?.name}
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="p-1.5 text-[#8B9DC3] hover:text-white hover:bg-[#1D3A5C] rounded transition group relative"
              title="로그아웃"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition whitespace-nowrap z-50">
                로그아웃
              </div>
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
